/**
 * Service API pour le checkout donation publique (Stripe + PayPal).
 *
 * Couche 1 — Transport (ADR-001). Endpoints publics utilisés depuis
 * les pages /donate, /c/.../payment, /pay/..., /campaigns/donate, etc.
 *
 * Utilise `apiCall` du data-layer en mode public (skipAuth +
 * skipOrganizationId). Les controllers `useStripeDonationCheckout` et
 * `usePaypalDonationCheckout` consomment ce service.
 */

import { apiCall } from "@donoor/data-layer";

const PUBLIC_OPTS = { skipAuth: true, skipOrganizationId: true } as const;

// ─── Stripe ─────────────────────────────────────────────────────────

export interface StripeCreatePaymentResponse {
  success: boolean;
  data?: { client_secret: string; payment_intent_id: string };
  message?: string;
}

export interface StripeCreateSetupResponse {
  success: boolean;
  data: { setup_secret: string; setup_intent_id: string };
  message?: string;
}

export interface StripeCreateSubscriptionResponse {
  success: boolean;
  data: {
    client_secret: string;
    subscription_id: string;
    stripe_account_id?: string;
  };
  message?: string;
}

export const stripeDonationApi = {
  /** Crée un PaymentIntent (don one-time). */
  createPaymentIntent(
    body: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<StripeCreatePaymentResponse> {
    return apiCall<StripeCreatePaymentResponse>(
      "/api/public/transactions/create-payment",
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        ...PUBLIC_OPTS,
      },
    );
  },

  /** Crée un SetupIntent pour subscription Stripe. */
  createRecurringSetup(
    body: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<StripeCreateSetupResponse> {
    return apiCall<StripeCreateSetupResponse>(
      "/api/public/transactions/create-recurring-setup",
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        ...PUBLIC_OPTS,
      },
    );
  },

  /** Crée la subscription Stripe associée à un SetupIntent. */
  createRecurring(
    body: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<StripeCreateSubscriptionResponse> {
    return apiCall<StripeCreateSubscriptionResponse>(
      "/api/public/transactions/create-recurring",
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        ...PUBLIC_OPTS,
      },
    );
  },

  /**
   * Apple Pay / Google Pay : sync l'email du wallet sur le PaymentIntent
   * APRÈS confirmation pour que le webhook puisse envoyer le reçu fiscal
   * au bon donateur. Non bloquant.
   */
  updateBuyerInfo(payload: {
    payment_intent_id: string;
    donor_email: string;
    donor_name?: string;
    organization_id?: string | number | null;
  }): Promise<unknown> {
    return apiCall(
      "/api/public/transactions/update-buyer-info",
      { method: "POST", body: payload, ...PUBLIC_OPTS },
    );
  },
};

// ─── PayPal ─────────────────────────────────────────────────────────

export interface PaypalCreateOrderResponse {
  success: boolean;
  data?: { order_id: string };
  message?: string;
}

export interface PaypalCreateSubscriptionResponse {
  success: boolean;
  data?: { subscription_id: string };
  message?: string;
}

export interface PaypalCaptureResponse {
  success: boolean;
  data?: {
    order_id: string;
    order_status: string;
    capture_id?: string;
    payer_email?: string | null;
    payer_name?: string | null;
  };
  message?: string;
}

export const paypalDonationApi = {
  /** Crée un PayPal Order (don one-time, Connected Path Multiparty). */
  createOrder(
    body: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<PaypalCreateOrderResponse> {
    return apiCall<PaypalCreateOrderResponse>(
      "/api/public/paypal/create-order",
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        ...PUBLIC_OPTS,
      },
    );
  },

  /** Crée une PayPal Subscription (don récurrent). */
  createSubscription(
    body: Record<string, unknown>,
    idempotencyKey: string,
  ): Promise<PaypalCreateSubscriptionResponse> {
    return apiCall<PaypalCreateSubscriptionResponse>(
      "/api/public/paypal/create-subscription",
      {
        method: "POST",
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        ...PUBLIC_OPTS,
      },
    );
  },

  /**
   * Capture server-side d'un order PayPal après approval donateur.
   * REQUIS pour finaliser le paiement (sans cet appel, l'order reste
   * en APPROVED et n'est jamais converti en transaction réelle).
   */
  captureOrder(
    orderId: string,
    body: {
      organization_id?: string | number | null;
      /** Message libre du donateur (cagnotte/QR) → transactions.donor_message. */
      message?: string | null;
      /**
       * Donateur saisi sur la page — seule source pour un paiement par CARTE
       * via PayPal (pas de compte PayPal, donc ni nom ni e-mail renvoyés).
       */
      donor_email?: string | null;
      donor_name?: string | null;
      is_anonymous?: boolean;
    },
  ): Promise<PaypalCaptureResponse> {
    return apiCall<PaypalCaptureResponse>(
      `/api/public/paypal/capture/${orderId}`,
      { method: "POST", body, ...PUBLIC_OPTS },
    );
  },
};
